// MainPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback, useContext } from "react";
import "../App.css";
import "leaflet/dist/leaflet.css";

import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";

import { useDebounce } from "../useDebounce";
import { useSearchParams, useNavigate, NavLink, Link } from "react-router-dom";
import dayjs from "dayjs";

import "bootstrap";
import AuthModal from "../auth/AuthModal";
import CreateEventModal from "./CreateEventModal";
import EventFilters from "../components/EventFilters";
import useWatchlist from "../hooks/useWatchlist";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CITY_CENTER = [52.2297, 21.0122];

function MapCenterOnLocation({ center }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.flyTo(center, map.getZoom());
  }, [center, map]);
  return null;
}

function MapFly({ focusedEventId, markerRefs }) {
  const map = useMap();

  useEffect(() => {
    if (!focusedEventId) return;

    const marker = markerRefs.current[focusedEventId];
    if (!marker) return;

    const targetLatLng = marker.getLatLng();
    map.flyTo(targetLatLng, 16, { duration: 0.6 });

    const handleMoveEnd = () => {
      if (marker._map) marker.openPopup();
      map.off("moveend", handleMoveEnd);
    };

    map.on("moveend", handleMoveEnd);

    return () => map.off("moveend", handleMoveEnd);
  }, [focusedEventId, markerRefs, map]);

  return null;
}

// Sprawdza, czy URI do obrazka jest aktywne
async function validateImageURL(url) {
  if (!url) return false;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

function ExpandableSection({ isOpen, children }) {
  const contentRef = useRef(null);

  return (
    <div
      className={`expandable-section ${isOpen ? "open" : ""}`}
      style={{
        transform: isOpen ? "scaleY(1)" : "scaleY(0)",
        opacity: isOpen ? 1 : 0,
      }}
    >
      <div ref={contentRef} className="expandable-inner">
        {children}
      </div>
    </div>
  );
}

const markerColorByWatched = (is_watched) => {
  if (is_watched === true) return "#640acaff";
  return "#e97e26ff";
};

const divIconFor = (color = "#888") =>
  L.divIcon({
    className: "custom-marker",
    html: `<div class="pulse" style="width:20px;height:20px;background:${color};border-radius:50%;border:3px solid white;box-shadow:0 6px 18px rgba(0,0,0,.2)"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

export default function App() {
  const navigate = useNavigate();
  const { user, accessToken, logout } = useContext(require("../auth/AuthContext").AuthContext);

  // ====== UI / RESPONSIVE ======
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 992; // jedyne źródło prawdy
  const [mobileView, setMobileView] = useState("list"); // "list" | "map"
  const [mobileBarHidden, setMobileBarHidden] = useState(true); // ukrywa cały panel (tabs+switch+filtry)

  // ====== DATA / FILTERS ======
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 400);

  // !!! FIX: selectedCategories musi być zadeklarowane PRZED filtersKey/useMemo
  const [selectedCategories, setSelectedCategories] = useState([]);

  const DEFALUT_SORT = "new";
  const [sort, setSort] = useState(DEFALUT_SORT);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [priceFilters, setPriceFilters] = useState({ free: false, paid: false });
  const [locationFilters, setLocationFilters] = useState({ live: false, online: false });

  const [locationRadius, setLocationRadius] = useState({
    lat: null,
    lng: null,
    radiusKm: 10,
    label: "",
  });
  const [locationText, setLocationText] = useState("");

  const [tab, setTab] = useState("all-events"); // "all-events" | "watched"
  const [expandedEvent, setExpandedEvent] = useState(null);

  // ====== SELECTION / MAP ======
  const [selected, setSelected] = useState(null);
  const [focusedEventId, setFocusedEventId] = useState(null);
  const [selectedCoord, setSelectedCoord] = useState(CITY_CENTER);

  const markerRefs = useRef({});
  const mapRef = useRef(null);
  const loadMoreRef = useRef(null);

  // ====== PAGINATION ======
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);

  const { toggle, isAuthenticated } = useWatchlist();

  function pushGtmEvent(eventName, params = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...params,
    });
  }

  // Porzucamy light mode: zawsze ustawiamy dark
  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", "dark");
    localStorage.setItem("theme", "dark");
  }, []);

  // Resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    // init
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Gdy zmienia się lokalizacja z radius filtra -> ustaw środek mapy
  useEffect(() => {
    if (locationRadius?.lat && locationRadius?.lng) {
      setSelectedCoord([locationRadius.lat, locationRadius.lng]);
    } else {
      setSelectedCoord(CITY_CENTER);
    }
  }, [locationRadius]);

  // Po przełączeniu na mapę: domyślnie pokaż panel, ale daj userowi "Schowaj filtry"
  // useEffect(() => {
  //   if (!isMobile) return;
  //   setMobileBarHidden(false);
  // }, [isMobile, mobileView]);

  // Hide-on-scroll (tylko na mobile i tylko gdy aktualny widok ma scroller)
  useEffect(() => {
    if (!isMobile) return;

    const listEl = document.querySelector(".event-list");
    // mapa sama w sobie nie scrolluje – panel chowamy przy scrollu listy;
    // na mapie user chowając panel robi to przyciskiem
    const scroller = listEl;

    if (!scroller) return;

    let lastY = scroller.scrollTop || 0;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const y = scroller.scrollTop || 0;
        const dy = y - lastY;

        // deadzone
        if (dy > 10) setMobileBarHidden(true);
        else if (dy < -10) setMobileBarHidden(false);

        lastY = y;
        ticking = false;
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  // Gdy mapa jest widoczna na mobile i zmienia się layout, Leaflet lubi się "rozjechać"
  useEffect(() => {
    if (!isMobile) return;
    if (mobileView !== "map") return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        mapRef.current?.invalidateSize?.();
      }, 60);
    });
  }, [isMobile, mobileView, mobileBarHidden]);

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        q: debouncedQuery.trim(),
        categories: selectedCategories,
        price: priceFilters,
        locationTypes: locationFilters,
        dateFrom,
        dateTo,
        sort,
        locationRadius: locationRadius
          ? {
              lat: locationRadius.lat,
              lng: locationRadius.lng,
              radiusKm: locationRadius.radiusKm,
              label: locationRadius.label,
            }
          : null,
        tab,
      }),
    [
      debouncedQuery,
      selectedCategories,
      priceFilters,
      locationFilters,
      dateFrom,
      dateTo,
      sort,
      locationRadius,
      tab,
    ]
  );

  const prevFiltersKeyRef = useRef(filtersKey);

  function getGoogleMapsDirectionsUrl(event) {
    if (event.lat && event.lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${event.lat},${event.lng}&travelmode=transit`;
    }
    if (event.address) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        event.address
      )}&travelmode=transit`;
    }
    return null;
  }

  const handleSelectEvent = (eventId) => {
    setSelected(eventId);

    // przewiń listę
    setTimeout(() => {
      document.getElementById(`event-${eventId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 250);
  };

  const createClusterCustomIcon = (cluster) => {
    const count = cluster.getChildCount();
    return L.divIcon({
      html: `<div class="cluster-icon">${count}</div>`,
      className: "event-cluster-icon",
      iconSize: L.point(40, 40, true),
    });
  };

  const onUpdateEvent = useCallback((id, patch) => {
    setEvents((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }, []);

  const handleToggleWatch = async (eventObj, ev) => {
    ev.stopPropagation();

    if (!isAuthenticated) {
      const el = document.getElementById("authModal");
      if (el && window.bootstrap?.Modal) {
        const modal = window.bootstrap.Modal.getOrCreateInstance(el);
        modal.show();
      }
      // pozwalamy iść dalej, ale revert jeśli backend odrzuci
    }

    const prev = !!eventObj.is_watched;
    onUpdateEvent(eventObj.id, { is_watched: !prev });

    const res = await toggle(eventObj.id, prev);
    if (!res.ok) onUpdateEvent(eventObj.id, { is_watched: prev });
  };

  const handleClearFilters = () => {
    setQuery("");
    setSelectedCategories([]);
    setPriceFilters({ free: false, paid: false });
    setLocationFilters({ online: false, live: false });
    setDateFrom("");
    setDateTo("");
    setSort(DEFALUT_SORT);
    setLocationText("");
    setLocationRadius({ lat: null, lng: null, radiusKm: 10, label: "" });
  };

  // Init z URL
  useEffect(() => {
    const q = searchParams.get("q") || "";
    const categoryNames = (searchParams.get("category") || "").split(",").filter(Boolean);

    const price_type = (searchParams.get("price_type") || "").split(",");
    const date_from = searchParams.get("date_from") || "";
    const date_to = searchParams.get("date_to") || "";
    const sortParam = searchParams.get("sort") || DEFALUT_SORT;
    const locationType = (searchParams.get("location_type") || "").split(",");

    const lat = searchParams.get("lat") || "";
    const lng = searchParams.get("lng") || "";
    const radiusKm = searchParams.get("radius_km");
    const locationLabel = searchParams.get("location_label") || "";

    setQuery(q);
    setSelectedCategories(categoryNames);

    setPriceFilters({
      free: price_type.includes("free"),
      paid: price_type.includes("paid"),
    });

    setLocationFilters({
      online: locationType.includes("online"),
      live: locationType.includes("live"),
    });

    setDateFrom(date_from);
    setDateTo(date_to);
    setSort(sortParam);

    // jeżeli nie ma lat/lng w URL, zostaw null
    const latNum = lat ? Number(lat) : null;
    const lngNum = lng ? Number(lng) : null;

    setLocationRadius({
      lat: Number.isFinite(latNum) ? latNum : null,
      lng: Number.isFinite(lngNum) ? lngNum : null,
      radiusKm: radiusKm ? Number(radiusKm) : 10,
      label: locationLabel || "",
    });

    setInitializedFromUrl(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch events (paginacja + reset na zmianę filtrów)
  useEffect(() => {
    if (!initializedFromUrl) return;

    const filtersChanged = prevFiltersKeyRef.current !== filtersKey;
    if (filtersChanged) {
      prevFiltersKeyRef.current = filtersKey;

      if (page !== 1) {
        setPage(1);
        setHasMore(true);
        return;
      }
      setHasMore(true);
    }

    const baseParams = new URLSearchParams();

    if (debouncedQuery.trim()) baseParams.set("q", debouncedQuery.trim());

    if (selectedCategories.length > 0) baseParams.set("category", selectedCategories.join(","));

    const priceTypes = [];
    if (priceFilters.free) priceTypes.push("free");
    if (priceFilters.paid) priceTypes.push("paid");
    if (priceTypes.length > 0) {
      // priceTypes.push("no info");
      baseParams.set("price_type", priceTypes.join(","));
    }

    const locationTypes = [];
    if (locationFilters.online) locationTypes.push("online");
    if (locationFilters.live) locationTypes.push("live");
    if (locationTypes.length > 0) baseParams.set("location_type", locationTypes.join(","));

    if (dateFrom) baseParams.set("date_from", dateFrom);
    if (dateTo) baseParams.set("date_to", dateTo);

    if (locationRadius?.lat && locationRadius?.lng) {
      baseParams.set("lat", String(locationRadius.lat));
      baseParams.set("lng", String(locationRadius.lng));
      if (locationRadius.radiusKm) baseParams.set("radius_km", String(locationRadius.radiusKm));
      if (locationRadius.label) baseParams.set("location_label", locationRadius.label);
    }

    baseParams.set("sort", sort);

    if (filtersChanged && page === 1) {
      setSearchParams(baseParams, { replace: true });
    }
    // URL w przeglądarce (bez page/page_size)
    // setSearchParams(baseParams);

    //   if (filtersChanged && page === 1) {
    //   const qVal = debouncedQuery.trim();

    //   const priceTypes = [];
    //   if (priceFilters.free) priceTypes.push("free");
    //   if (priceFilters.paid) priceTypes.push("paid");

    //   const locationTypes = [];
    //   if (locationFilters.online) locationTypes.push("online");
    //   if (locationFilters.live) locationTypes.push("live");

    //   const hasLocation = !!(locationRadius?.lat && locationRadius?.lng);

    //   pushGtmEvent("events_filters_applied", {
    //     q: qVal || undefined,
    //     categories: selectedCategories?.length ? selectedCategories.join(",") : undefined,
    //     price_type: priceTypes.length ? priceTypes.join(",") : undefined,
    //     location_type: locationTypes.length ? locationTypes.join(",") : undefined,
    //     date_from: dateFrom || undefined,
    //     date_to: dateTo || undefined,
    //     sort: sort || undefined,
    //     tab: tab || undefined,
    //     has_location: hasLocation,
    //     radius_km: hasLocation ? (locationRadius.radiusKm || 10) : undefined,
    //   });
    // }

    // API params
    const apiParams = new URLSearchParams(baseParams);
    apiParams.set("page", String(page));
    apiParams.set("page_size", "100");
    if (priceTypes.length > 0) apiParams.set("price_type", [...priceTypes, "no info"].join(","));

    let aborted = false;

    const fetchEvents = async () => {
      try {
        if (page === 1) setLoading(true);
        else setIsFetchingMore(true);

        const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;

        let url = `${API_BASE}/api/events/?${apiParams.toString()}`;
        if (tab === "watched" && user) url += `&is_watched=true`;

        const res = await fetch(url, { headers });
        const data = await res.json();

        if (aborted) return;

        const rawEvents = data.results || [];
        const nextUrl = data.next;

        const mapped = await Promise.all(
          rawEvents.map(async (e, idx) => {
            const lat = parseFloat(e.lat);
            const lng = parseFloat(e.lng);

            let cover = e.image || "";
            if (cover && !cover.startsWith("http")) cover = `${window.location.origin}${cover}`;

            const valid = await validateImageURL(cover);
            if (!valid) {
              cover =
                "https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=1200&auto=format&fit=crop";
            }

            return {
              id: e.id || idx,
              title: e.name || "Bez nazwy",
              place: e.venue_name || e.address || "",
              date: e.start_datetime ? dayjs(e.start_datetime).format("YYYY-MM-DD HH:mm") : "",
              popularity: parseInt(e.ext_interested_count || 0, 10),
              link: e.ext_event_url || "",
              primary: e.category_names,
              coords:
                Number.isFinite(lat) &&
                Number.isFinite(lng) &&
                e.location_type !== "online"
                  ? [lat, lng]
                  : null,
              cover,
              isFree: e.price_type || "no_info",
              price: e.price_from || "",
              organizer: e.ext_creator_name || "",
              organizer_link: e.ext_creator_url || "",
              description: e.description || "",
              categories: e.category_names || [],
              price_type_label: e.price_type_label || "",
              location_type: e.location_type || "",
              address: e.address || "",
              is_watched: !!e.is_watched,
              lat,
              lng,
            };
          })
        );

        setEvents((prev) => (page === 1 ? mapped : [...prev, ...mapped]));
        setHasMore(Boolean(nextUrl));
      } catch (err) {
        if (!aborted) console.error(err);
      } finally {
        if (!aborted) {
          setLoading(false);
          setIsFetchingMore(false);
        }
      }
    };

    fetchEvents();

    return () => {
      aborted = true;
    };
  }, [
    initializedFromUrl,
    filtersKey,
    page,
    user,
    tab,
    accessToken,
    debouncedQuery,
    selectedCategories,
    priceFilters,
    locationFilters,
    dateFrom,
    dateTo,
    sort,
    locationRadius,
    setSearchParams,
  ]);

  // Reset paginacji na zmianę filtrów (po init)
  useEffect(() => {
    if (!initializedFromUrl) return;
    setPage(1);
    setHasMore(true);
  }, [
    initializedFromUrl,
    debouncedQuery,
    selectedCategories,
    priceFilters,
    locationFilters,
    dateFrom,
    dateTo,
    sort,
    locationRadius,
    tab,
  ]);

  // Infinite scroll sentinel – root = event-list (bo to ona scrolluje)
  useEffect(() => {
    if (!hasMore) return;

    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const rootEl = document.querySelector(".event-list") || null;
    if (!rootEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading && !isFetchingMore) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: rootEl,
        rootMargin: "220px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, isFetchingMore, mobileView, isMobile]);

  const markers = useMemo(() => events.filter((e) => e.coords), [events]);

  return (
    <div className="app events-shell d-flex flex-column">
      <div className="main-content">
        {/* NAVBAR */}
<nav className={`navbar navbar-expand-lg glass sticky-top shadow-sm px-3 ${isMobile ? "main-navbar--mobile" : ""}`}>
  <div className="container-fluid">
    {(() => {
      const Brand = (
        <div
          className="main-navbar__logo"
          onClick={() => navigate("/")}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <img
            src="/siedzieje_logo.webp"
            alt="SieDzieje"
            className="main-navbar__logoImg"
            draggable="false"
          />
        </div>
      );

      const AboutUsLink = (
        <NavLink
          to="/o-nas"
          className={({ isActive }) =>
            `main-navbar__iconBtn main-navbar__iconBtn--ghost ${isActive ? "is-active" : ""}`
          }
          aria-label="O nas"
          title="O nas"
        >
          <i className="bi bi-info-circle-fill" />
        </NavLink>
      );

      const Filters = (
        <span>
          <EventFilters
            query={query}
            onQueryChange={setQuery}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateChange={(from, to) => {
              setDateFrom(from);
              setDateTo(to);
            }}
            sort={sort}
            onSortChange={setSort}
            selectedCategories={selectedCategories}
            onSelectedCategoriesChange={setSelectedCategories}
            priceFilters={priceFilters}
            onPriceFiltersChange={setPriceFilters}
            locationFilters={locationFilters}
            onLocationFiltersChange={setLocationFilters}
            locationRadius={locationRadius}
            onLocationRadiusChange={setLocationRadius}
            isCompact={isMobile}
            onClearFilters={handleClearFilters}
            locationText={locationText}
            onLocationTextChange={setLocationText}
          />
        </span>
      );

      // Desktop actions zostają (z tekstem + dropdown)
      const DesktopActions = (
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-primary btn-sm rounded-pill"
            onClick={() => {
              const targetId = user ? "createEventModal" : "authModal";
              const modalEl = document.getElementById(targetId);

              if (modalEl && window.bootstrap?.Modal) {
                const instance =
                  window.bootstrap.Modal.getInstance(modalEl) ||
                  new window.bootstrap.Modal(modalEl);
                instance.show();
              }
            }}
          >
            <i className="bi bi-plus-circle me-1"></i> Dodaj wydarzenie
          </button>

          {!user ? (
            <button
              className="btn btn-primary btn-sm rounded-pill"
              data-bs-toggle="modal"
              data-bs-target="#authModal"
            >
              <i className="bi bi-person-fill me-1"></i> Zaloguj się
            </button>
          ) : (
            <div className="nav-profile-dropdown">
              <button
                className="btn btn-light btn-sm rounded-pill d-flex align-items-center gap-2 dropdown-toggle"
                data-bs-toggle="dropdown"
                data-bs-display="static"
                aria-expanded="false"
              >
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt="avatar"
                    className="rounded-circle"
                    style={{ width: "24px", height: "24px", objectFit: "cover" }}
                  />
                ) : (
                  <i className="bi bi-person-circle fs-5 text-primary"></i>
                )}
                <span className="fw-semibold">{user.username}</span>
              </button>

              <ul className="dropdown-menu dropdown-menu-end shadow-sm glass">
                <li>
                  <button className="dropdown-item" onClick={() => navigate("/profile")}>
                    <i className="bi bi-person me-2"></i> Mój profil
                  </button>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => navigate("/profile/settings")}>
                    <i className="bi bi-gear me-2"></i> Ustawienia konta
                  </button>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <button className="dropdown-item text-danger" onClick={logout}>
                    <i className="bi bi-box-arrow-right me-2"></i> Wyloguj się
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      );

      // Mobile actions: dwa CTA bez tekstu
      // Mobile actions: dwa CTA bez tekstu (plus + profil dropdown)
        const MobileActions = (
          <div className="main-navbar__mobileActions">
            <button
              type="button"
              className="main-navbar__iconBtn main-navbar__iconBtn--cta"
              aria-label="Dodaj wydarzenie"
              title="Dodaj wydarzenie"
              onClick={() => {
                const targetId = user ? "createEventModal" : "authModal";
                const modalEl = document.getElementById(targetId);

                if (modalEl && window.bootstrap?.Modal) {
                  const instance =
                    window.bootstrap.Modal.getInstance(modalEl) ||
                    new window.bootstrap.Modal(modalEl);
                  instance.show();
                }
              }}
            >
              <i className="bi bi-plus-lg" />
            </button>

            {!user ? (
              <button
                type="button"
                className="main-navbar__iconBtn main-navbar__iconBtn--ghost"
                aria-label="Zaloguj się"
                title="Zaloguj się"
                data-bs-toggle="modal"
                data-bs-target="#authModal"
              >
                <i className="bi bi-person-fill" />
              </button>
            ) : (
              <div className="nav-profile-dropdown">
                <button
                  type="button"
                  className="main-navbar__iconBtn main-navbar__iconBtn--ghost dropdown-toggle"
                  data-bs-toggle="dropdown"
                  data-bs-display="static"
                  aria-expanded="false"
                  aria-label="Menu profilu"
                  title="Menu profilu"
                >
                  {user.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt="avatar"
                      className="main-navbar__avatar"
                    />
                  ) : (
                    <i className="bi bi-person-circle" />
                  )}
                </button>

                <ul className="dropdown-menu dropdown-menu-end shadow-sm glass">
                  <li>
                    <button className="dropdown-item" onClick={() => navigate("/profile")}>
                      <i className="bi bi-person me-2"></i> Mój profil
                    </button>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={() => navigate("/profile/settings")}>
                      <i className="bi bi-gear me-2"></i> Ustawienia konta
                    </button>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item text-danger" onClick={logout}>
                      <i className="bi bi-box-arrow-right me-2"></i> Wyloguj się
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        );


      // RENDER
      return isMobile ? (
        <div className="main-navbar__mobileGrid">
          <div className="main-navbar__mobileLeft">{AboutUsLink}</div>
          <div className="main-navbar__mobileCenter">{Brand}</div>
          <div className="main-navbar__mobileRight">{MobileActions}</div>
        </div>
      ) : (
        <>
          {Brand}
          {Filters}
          {DesktopActions}
          {/* AboutUs jako ikonka po prawej */}
          <div className="ms-3">{AboutUsLink}</div>
        </>
      );
    })()}
  </div>
</nav>

<AuthModal modalId="authModal" />
<CreateEventModal mode="create" />


        {/* TREŚĆ: lista + mapa */}
        <div className="content-split flex-grow-1 d-flex flex-column flex-lg-row position-relative">
          {/* MOBILE PANEL (tabs + switch + filters) */}
          {isMobile && !mobileBarHidden && (
            <div className="mobile-toolbar">
              <div className="mobile-toolbar__inner glass shadow-sm">
                {/* Top row: tabs + hide button */}
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm rounded-pill flex-shrink-0"
                    onClick={() => setMobileBarHidden(true)}
                    aria-label="Schowaj filtry"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <i className="bi bi-chevron-up me-1"></i> Schowaj filtry
                  </button>
                  <ul className="nav nav-pills events-tabs justify-content-center mb-0 flex-grow-1">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`tab-pill ${tab === "all-events" ? "tab-pill--active" : ""}`}
                        onClick={() => setTab("all-events")}
                      >
                        <i className="bi bi-calendar-event me-1" />
                        Wszystkie
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`tab-pill ${tab === "watched" ? "tab-pill--active" : ""}`}
                        onClick={() => setTab("watched")}
                      >
                        <i className="bi bi-heart me-1" />
                        Obserwowane
                      </button>
                    </li>
                  </ul>

                  {/* <button
                    type="button"
                    className="btn btn-outline-primary btn-sm rounded-pill flex-shrink-0"
                    onClick={() => setMobileBarHidden(true)}
                    aria-label="Schowaj filtry"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <i className="bi bi-chevron-up me-1"></i> Schowaj filtry
                  </button> */}
                </div>

                {/* Switch: list/map */}
                <div className="view-switch" role="tablist" aria-label="Widok">
                  <button
                    type="button"
                    className={`view-switch__btn ${mobileView === "list" ? "is-active" : ""}`}
                    onClick={() => setMobileView("list")}
                  >
                    <i className="bi bi-list-ul me-1" />
                    Lista
                  </button>
                  <button
                    type="button"
                    className={`view-switch__btn ${mobileView === "map" ? "is-active" : ""}`}
                    onClick={() => setMobileView("map")}
                  >
                    <i className="bi bi-map me-1" />
                    Mapa
                  </button>
                  
                </div>

                {/* Filters */}
                <div className="mt-2">
                  <EventFilters
                    query={query}
                    onQueryChange={setQuery}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={(from, to) => {
                      setDateFrom(from);
                      setDateTo(to);
                    }}
                    sort={sort}
                    onSortChange={setSort}
                    selectedCategories={selectedCategories}
                    onSelectedCategoriesChange={setSelectedCategories}
                    priceFilters={priceFilters}
                    onPriceFiltersChange={setPriceFilters}
                    locationFilters={locationFilters}
                    onLocationFiltersChange={setLocationFilters}
                    locationRadius={locationRadius}
                    onLocationRadiusChange={setLocationRadius}
                    isCompact={true}
                    onClearFilters={handleClearFilters}
                    locationText={locationText}
                    onLocationTextChange={setLocationText}
                  />
                </div>
                
              </div>
            </div>
          )}

          {/* MOBILE REVEAL BUTTON (duży + tekst) */}
          {isMobile && mobileBarHidden && (
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 1200,
                padding: "10px 12px",
                pointerEvents: "none",
              }}
            >
              <button
                type="button"
                className="btn btn-primary rounded-pill w-100"
                onClick={() => setMobileBarHidden(false)}
                aria-label="Pokaż filtry"
                style={{ pointerEvents: "auto", padding: "12px 14px", fontWeight: 700 }}
              >
                <i className="bi bi-sliders me-2"></i>
                Pokaż filtry
              </button>
            </div>
          )}

          {/* LISTA */}
          {(!isMobile || mobileView === "list") && (
            <div className="d-flex flex-column col-12 col-lg-5">
              {/* Desktop tabs */}
              <div className="ev-bookmarks-panel d-none d-lg-block px-3 pt-3 col-lg-12">
                <ul className="nav nav-pills events-tabs mb-3 justify-content-center">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`tab-pill ${tab === "all-events" ? "tab-pill--active" : ""}`}
                      onClick={() => setTab("all-events")}
                    >
                      <i className="bi bi-calendar-event me-1" />
                      Wszystkie wydarzenia
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`tab-pill ${tab === "watched" ? "tab-pill--active" : ""}`}
                      onClick={() => setTab("watched")}
                    >
                      <i className="bi bi-heart me-1" />
                      Obserwowane
                    </button>
                  </li>
                </ul>
              </div>

              <div className="event-list overflow-auto p-3 custom-scroll">
                {/* SKELETON */}
                {loading && (
                  <div className="d-flex flex-column gap-3">
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <div
                        key={`sk-${idx}`}
                        className="card event-card mb-0 border-0 shadow-sm"
                        aria-hidden="true"
                      >
                        <div
                          className="rounded-top"
                          style={{
                            height: 140,
                            background:
                              "linear-gradient(90deg, rgba(0,0,0,0.04), rgba(0,0,0,0.10), rgba(0,0,0,0.04))",
                            backgroundSize: "200% 100%",
                            animation: "sd-shimmer 1.2s ease-in-out infinite",
                          }}
                        />
                        <div className="card-body">
                          <div className="placeholder-glow">
                            <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                              <span className="placeholder col-7 rounded-3" />
                              <span className="placeholder col-2 rounded-3" />
                            </div>

                            <div className="d-flex flex-wrap gap-2 mb-3">
                              <span className="placeholder col-4 rounded-pill" />
                              <span className="placeholder col-3 rounded-pill" />
                              <span className="placeholder col-2 rounded-pill" />
                            </div>

                            <span className="placeholder col-12 rounded-3 mb-2" />
                            <span className="placeholder col-10 rounded-3 mb-2" />
                            <span className="placeholder col-8 rounded-3" />

                            <div className="d-flex justify-content-between align-items-center mt-3">
                              <div className="d-flex gap-2 flex-wrap">
                                <span className="placeholder col-2 rounded-pill" />
                                <span className="placeholder col-3 rounded-pill" />
                                <span className="placeholder col-2 rounded-pill" />
                              </div>
                              <span className="placeholder col-3 rounded-pill" style={{ height: 32 }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* EMPTY */}
                {!loading && events.length === 0 && (
                  <div className="text-center text-secondary my-5">
                    <i className="bi bi-inbox fs-1 mb-3"></i>
                    <p className="mb-0">Brak wydarzeń spełniających kryteria.</p>
                  </div>
                )}

                {/* WATCHED + NOT LOGGED */}
                {!loading && tab === "watched" && !user ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-person-circle fs-1 mb-3"></i>
                    <p className="mb-0">Zaloguj się aby zobaczyć obserwowane wydarzenia</p>
                    <p></p>
                    <button
                      className="btn btn-outline-primary btn-sm rounded-pill"
                      data-bs-toggle="modal"
                      data-bs-target="#authModal"
                    >
                      <i className="bi bi-box-arrow-in-right me-1"></i> Zaloguj się
                    </button>
                  </div>
                ) : (
                  !loading &&
                  events.map((e) => (
                    <div
                      key={e.id}
                      id={`event-${e.id}`}
                      className={`card event-card mb-3 ${selected === e.id ? "active" : ""}`}
                      onClick={() => {
                        handleSelectEvent(e.id);
                        setFocusedEventId(e.id);
                        setSelected(e.id);
                      }}
                      onDoubleClick={(ev) => {
                        ev.stopPropagation();
                        setExpandedEvent((prev) => (prev === e.id ? null : e.id));
                        setFocusedEventId(e.id);
                        handleSelectEvent(e.id);
                      }}
                    >
                      <div className="cover" style={{ backgroundImage: `url("${e.cover}")` }}></div>

                      <button
                        type="button"
                        className={`btn-fav-icon ${e.is_watched ? "btn-fav-icon--active" : ""}`}
                        onClick={(ev) => handleToggleWatch(e, ev)}
                      >
                        <i className={`bi ${e.is_watched ? "bi-heart-fill" : "bi-heart"}`} aria-hidden="true"></i>
                      </button>

                      <div className="card-body d-flex flex-column justify-content-between">
                        <div>
                          <h5 className="card-title mb-2">{e.title}</h5>

                          {/* ikonki zawsze pomarańczowe (przez klasę text-primary, w CSS masz orange) */}
                          <div className="event-meta d-flex flex-wrap align-items-center gap-2 text-secondary small mb-2">
                            {e.place && (
                              <div className="meta-item d-flex align-items-center">
                                <i className="bi bi-geo-alt me-1 text-primary"></i>
                                <span>{e.place}</span>
                              </div>
                            )}
                            {e.date && (
                              <div className="meta-item d-flex align-items-center">
                                <i className="bi bi-calendar-event me-1 text-primary"></i>
                                <span>{e.date}</span>
                              </div>
                            )}
                            <div className="meta-item d-flex align-items-center">
                              <i className="bi bi-coin me-1 text-primary"></i>
                              <span>{e.price_type_label}</span>
                            </div>
                            {Number.isFinite(e.popularity) && (
                              <div className="meta-item d-flex align-items-center">
                                <i className="bi bi-eye me-1 text-primary"></i>
                                <span>{e.popularity}</span>
                              </div>
                            )}
                          </div>

                          {/* opis skrócony + "więcej" pomarańczowe */}
                          {e.description && expandedEvent !== e.id && (
                            <p className="small text-muted mb-0">
                              {e.description.slice(0, 100)}
                              {e.description.length > 100 && "... "}
                              <span
                                className="text-primary fw-semibold cursor-pointer"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setExpandedEvent(e.id);
                                  handleSelectEvent(e.id);
                                }}
                              >
                                więcej
                              </span>
                            </p>
                          )}

                          {expandedEvent === e.id && (
                            <ExpandableSection isOpen={expandedEvent === e.id}>
                              <div className="expanded-event mt-2">
                                <p
                                  className="small text-muted"
                                  style={{
                                    whiteSpace: "pre-line",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {e.description}
                                </p>

                                {e.organizer && (
                                  <p className="small mb-1">
                                    <i className="bi bi-person-circle me-1 text-primary"></i>
                                    <strong>Organizator:</strong>{" "}
                                    {e.organizer_link ? (
                                      <a
                                        href={e.organizer_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-decoration-none text-orange"
                                      >
                                        {e.organizer}
                                      </a>
                                    ) : (
                                      e.organizer
                                    )}
                                  </p>
                                )}

                                <p className="small mb-2">
                                  <i className="bi bi-link-45deg me-1 text-primary"></i>
                                  <Link
                                    to={`/event/${e.id}`}
                                    className="text-decoration-none text-orange"
                                    onClick={(ev) => ev.stopPropagation()}
                                  >
                                    Otwórz wydarzenie
                                  </Link>
                                </p>

                                <button
                                  className="btn btn-sm btn-outline-secondary rounded-pill"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setExpandedEvent(null);
                                  }}
                                >
                                  <i className="bi bi-chevron-up me-1"></i> Zwiń
                                </button>
                              </div>
                            </ExpandableSection>
                          )}
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2 gap-2">
                          <div className="d-flex flex-wrap gap-1">
                            {(e.categories || []).map((t) => (
                              <span
                                key={t}
                                className="badge text-bg-primary text-white px-2 py-1 rounded-pill"
                              >
                                {t}
                              </span>
                            ))}
                          </div>

                          <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            <Link
                              to={`/event/${e.id}`}
                              className="btn btn-primary btn-sm rounded-pill"
                              onClick={(ev) => ev.stopPropagation()}
                              target="_blank" 
                              rel="noreferrer"
                            >
                              <i className="bi bi-open"></i> Otwórz
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Infinite scroll sentinel */}
                {!loading && <div ref={loadMoreRef} style={{ height: 1 }} />}

                {!loading && isFetchingMore && (
                  <div className="text-center py-2 small text-muted">Ładowanie kolejnych wydarzeń...</div>
                )}

                {!loading && !hasMore && events.length > 0 && tab === "all-events" && (
                  <div className="text-center py-2 small text-muted">
                    To już wszystkie wydarzenia dla wybranych filtrów.
                  </div>
                )}

                {!loading && !hasMore && events.length > 0 && tab === "watched" && (
                  <div className="text-center py-2 small text-muted">
                    To już wszystkie obserwowane przez Ciebie wydarzenia dla wybranych filtrów
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MAPA */}
          {(!isMobile || mobileView === "map") && (
            <div className="col-12 col-lg-7 flex-grow-1">
              <MapContainer
                className="w-100 h-100 rounded-4 overflow-hidden"
                center={selectedCoord}
                zoom={12}
                scrollWheelZoom
                whenReady={(e) => {
                  const map = e.target;
                  mapRef.current = map;

                  setTimeout(() => map.invalidateSize(), 60);

                  if (isMobile && mobileView === "map" && focusedEventId) {
                    let tries = 0;
                    const iv = setInterval(() => {
                      tries += 1;
                      const mrk = markerRefs.current?.[focusedEventId];

                      if (mrk && mrk.getLatLng) {
                        const ll = mrk.getLatLng();
                        map.setView(ll, Math.max(map.getZoom(), 14), { animate: true });
                        mrk.openPopup?.();
                        clearInterval(iv);
                      }

                      if (tries >= 6) clearInterval(iv);
                    }, 80);
                  }
                }}
              >
                {/* DARK tiles */}
                <TileLayer
                  attribution="&copy; OpenStreetMap • &copy; CARTO"
                  url="https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}{r}.png"
                />

                <MarkerClusterGroup
                  iconCreateFunction={createClusterCustomIcon}
                  maxClusterRadius={1}
                  spiderfyOnMaxZoom
                  showCoverageOnHover={false}
                  zoomToBoundsOnClick={false}
                  removeOutsideVisibleBounds={false}
                >
                  <MapFly focusedEventId={focusedEventId} markerRefs={markerRefs} />
                  <MapCenterOnLocation center={selectedCoord} />

                  {markers.map((e) => (
                    <Marker
                      key={e.id}
                      position={e.coords}
                      icon={divIconFor(markerColorByWatched(e.is_watched))}
                      ref={(ref) => (markerRefs.current[e.id] = ref)}
                      eventHandlers={{
                        click: () => {
                          setSelected(e.id);
                          setFocusedEventId(null);

                          // na mobile w widoku mapy nie zawsze jest lista w DOM – więc to scrollIntoView nie zawsze zadziała
                          setTimeout(() => {
                            document.getElementById(`event-${e.id}`)?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }, 300);
                        },
                      }}
                    >
                      <Popup>
                        <strong>{e.title}</strong>
                        <br />
                        {e.place && (
                          <>
                            📍 {e.place}
                            <br />
                          </>
                        )}
                        {e.date && (
                          <>
                            📅 {e.date}
                            <br />
                          </>
                        )}
                        <br />

                        {isMobile && (
                          <button
                            className="btn btn-primary btn-sm rounded-pill margin-top-2"
                            onClick={() => navigate("/event/" + e.id)}
                          >
                            <i className="bi bi-open"></i> Otwórz wydarzenie
                          </button>
                        )}

                        <button
                          className="btn btn-primary btn-sm rounded-pill"
                          onClick={() => window.open(getGoogleMapsDirectionsUrl(e), "_blank")}
                        >
                          <i className="bi bi-open"></i> Zobacz trasę w Google Maps
                        </button>
                      </Popup>
                      <Tooltip>{e.title}</Tooltip>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>
          )}
        </div>

        {/* Shimmer keyframes */}
        <style>{`
          @keyframes sd-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    </div>
  );
}